<script setup lang="ts">
type Props = {
  hideCloseButton?: boolean;
};

const { hideCloseButton = false } = defineProps<Props>();

type Emits = {
  close: [];
};

const emit = defineEmits<Emits>();

const router = useRouter();
const { t } = useTexts();
const { modalType } = storeToRefs(useLayoutStore());
const { openModal, closeModal } = useLayoutStore();
const { currentRouteName } = useRoutes();
const { isAuthenticated } = storeToRefs(useAuthStore());
const { logout: logoutAuth } = useAuthStore();
const { isMobile } = useLayout();

const { isSingleStepRegisterEnabled, openLogin, openRegister } = useAuth();

const isHelpPage = computed(() => {
  return currentRouteName.value === 'help' || modalType.value === 'help';
});

const isPendingPageOrModal = computed(() => {
  return (
    (currentRouteName.value === 'pending' ||
      modalType.value === 'verification' ||
      currentRouteName.value === 'migrate-confirm') &&
    isAuthenticated.value
  );
});

const isShowLoginButton = computed(() => {
  return (
    modalType.value === 'forgot-password' || modalType.value === 'register'
  );
});

const isSmallTopBar = computed(() => {
  return (
    (modalType.value === 'forgot-password' ||
      modalType.value === 'register' ||
      modalType.value === 'login') &&
    isSingleStepRegisterEnabled.value &&
    isMobile.value
  );
});

function onClickUpdateButton() {
  window.location.reload();
}

const onClickButtonLogout = async () => {
  if (confirm(t('verification.logout_confirmation'))) {
    await logoutAuth({
      displayMessage: true,
      redirectToHome: true,
      origin: 'user',
    });

    navigateTo(localeLink('/'));
  }
};

function onCloseButtonClick() {
  emit('close');
}

function onClickHelpButton() {
  openModal('help');
}

function onClickHelpBackButton() {
  if (modalType.value === 'help') {
    closeModal();

    return;
  }
  if (window.opener && window.opener !== window) {
    window.close();
  } else {
    router.back();
  }
}
</script>

<template>
  <header
    id="topBar"
    :class="[$style.newModalTopBar, { [$style.smallTopBar]: isSmallTopBar }]"
  >
    <div :class="$style.buttonsContainer">
      <button
        v-if="!isHelpPage"
        :class="$style.helpButton"
        @click="onClickHelpButton"
      >
        <NuxtIcon
          :class="$style.buttonIcon"
          name="q_and_a"
        />
        <p v-html="$t('multiStepRegister.top_bar.help')"> </p>
      </button>
      <button
        v-else-if="isHelpPage"
        :class="$style.backButton"
        @click="onClickHelpBackButton"
      >
        <NuxtIcon
          :class="[$style.buttonIcon, $style.smallIcon]"
          name="arrows/arrow_left"
        />
        <p v-html="$t('general.back')"> </p>
      </button>

      <button
        v-if="isPendingPageOrModal"
        :class="$style.updateButton"
        @click="onClickUpdateButton"
      >
        <NuxtIcon
          :class="[$style.buttonIcon, $style.smallIcon]"
          name="sync"
        />
      </button>
    </div>

    <div
      :class="[
        $style.rightSection,
        { [$style.isOnLeft]: isHelpPage },
        { [$style.withoutCloseButton]: hideCloseButton },
      ]"
    >
      <div
        v-if="isPendingPageOrModal"
        :class="$style.actionButton"
        @click="onClickButtonLogout()"
      >
        <span>{{ $t('modal.top_bar.no_time_now') }}</span>
        <a>{{ $t('modal.top_bar.logout') }}</a>
      </div>

      <div
        v-else-if="modalType === 'login'"
        :class="$style.actionButton"
        @click="openRegister()"
      >
        <span>{{ $t('modal.top_bar.have_no_account') }}</span>
        <a>{{ $t('modal.top_bar.create_account') }}</a>
      </div>

      <div
        v-else-if="isShowLoginButton"
        :class="$style.actionButton"
        @click="openLogin()"
      >
        <span> {{ $t('multiStepRegister.top_bar.have_account') }} </span>
        <a>{{ $t('multiStepRegister.top_bar.login') }}</a>
      </div>

      <BaseModalCloseButton
        v-if="!hideCloseButton"
        :class="$style.closeButton"
        is-inside-topbar
        @close="onCloseButtonClick"
      />
    </div>
  </header>
</template>

<style module lang="scss">
.newModalTopBar {
  @apply bg-black/20 h-12 min-h-12 md:h-16 md:min-h-16 px-2 md:px-4 flex justify-between items-center select-none;

  .buttonsContainer {
    @apply flex gap-2 min-h-8;

    .helpButton,
    .updateButton,
    .backButton {
      @apply flex items-center leading-none rounded-lg px-2.5 py-1 md:py-1.5 bg-auth-button-bg text-auth-button-text;

      .buttonIcon {
        @apply inline mr-2 text-auth-button-text/80 text-2xl;

        &.smallIcon {
          @apply text-sm mr-0 text-texts/30;
        }
      }

      p {
        @apply text-2xs font-light text-left leading-none;

        strong {
          @apply font-bold text-xs leading-none block -mb-px;
        }
      }
    }

    .updateButton {
      @apply bg-texts/10 text-texts/50 px-2;
    }

    .backButton {
      @apply bg-texts/10 text-texts/50 px-2 gap-2;

      p {
        @apply text-xs font-bold;
      }
    }
  }

  .rightSection {
    @apply flex gap-4 pr-10 md:pr-12;

    .actionButton {
      @apply text-right leading-none cursor-pointer flex flex-col gap-0.5 md:gap-1 justify-center;

      span {
        @apply text-2xs font-light text-white;
      }

      a {
        @apply text-xs font-bold text-primary;
      }
    }

    .closeButton {
      @apply text-white/60 text-lg leading-none -mt-0.5;
    }

    &.isOnLeft {
      .actionButton {
        @apply text-left;
      }
    }

    &.withoutCloseButton {
      @apply pr-1;
    }
  }

  &.smallTopBar {
    @apply bg-black/60 h-9 min-h-9 px-1.5;

    .buttonsContainer {
      @apply gap-1.5 min-h-6;

      .helpButton,
      .updateButton,
      .backButton {
        @apply px-2 py-0.5 rounded;

        .buttonIcon {
          @apply inline mr-1 text-base;

          &.smallIcon {
            @apply text-sm mr-0;
          }
        }

        p {
          @apply text-2xs font-semibold inline leading-3;

          br {
            @apply hidden;
          }

          strong {
            @apply font-semibold text-2xs inline mb-0;
          }
        }
      }

      .updateButton {
        @apply bg-texts/10 text-texts/50 px-2;
      }

      .backButton {
        @apply bg-texts/10 text-texts/50 px-2 gap-2;

        p {
          @apply text-xs font-bold;
        }
      }
    }

    .rightSection {
      @apply gap-3 pr-9;

      .actionButton {
        span {
          @apply hidden;
        }

        a {
          @apply text-xs;
        }
      }

      .closeButton {
        @apply text-xs leading-none w-7 h-7 overflow-hidden mr-px;
      }
    }
  }
}
</style>
